import React, { useEffect, useState } from "react";
import { useOutletContext, useParams, useNavigate, useLocation } from "react-router-dom";
import { Spin, Button, Drawer, Flex } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import ContactCompanyInfo from "../../common/ContactCompanyInfo";
import OrderShort from "../OrderShort";
import DetailPageTitle from "../DetailPageTitle";
import MarketPanel from "../../matching/MarketPanel";
import OffersPanel from "../../matching/OffersPanel";
import axios from "axios";
import { transportCategories, transportBodyTypes } from "../categories";
import {
  shippingType,
  loadType,
  orderType,
  currency,
  packageType,
} from "../constants";
import {
  paymentForms,
  paymentMethods,
} from "../../../../src/constants/transport";
import { useSelector } from "react-redux";
import * as orderStatus from "../../constants/status";
import { markSearchListReturn } from "../../../utils/searchReturnState";

const transportType = (type) => {
  if (!type) {
    return "";
  }
  const transportType = transportCategories.find((c) => +c.value === +type);
  if (!transportType) {
    return null;
  }
  return transportType;
};

const bodyType = (type) => {
  if (!type) {
    return "";
  }
  const transportType = transportBodyTypes.find((c) => +c.id === +type);
  if (!transportType) {
    return null;
  }
  return transportType.name;
};

const OrderDetail = () => {
  const { setShowOnMap } = useOutletContext();
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [_order, setOrder] = useState(null);
  const [order, setLocalOrder] = useState(_order || null);
  const [loading, setLoading] = useState(false);
  const [profile, setProfile] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 992);
  const [drawerOpen, setDrawerOpen] = useState(true);

  const user = useSelector((state) => state.auth.user);

  const handleBack = () => {
    const fromSearchList = location.state?.fromSearchList;
    if (fromSearchList === "orders" || fromSearchList === "transports") {
      markSearchListReturn(fromSearchList);
      navigate(location.state?.returnTo || (fromSearchList === "orders" ? "/loads" : "/transports"), {
        replace: true,
      });
      return;
    }

    const canGoBack = window.history.length > 1;
    canGoBack ? navigate(-1) : navigate("/");
  };

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 992);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    setDrawerOpen(true);
  }, [location.pathname, location.search]);

  useEffect(() => {
    const handleCloseDrawers = () => setDrawerOpen(false);
    window.addEventListener('closeMobileDrawers', handleCloseDrawers);
    return () => window.removeEventListener('closeMobileDrawers', handleCloseDrawers);
  }, []);

  useEffect(() => {
    document.body.classList.toggle('mobile-other-level-view', isMobile);

    return () => {
      document.body.classList.remove('mobile-other-level-view');
    };
  }, [isMobile]);

  useEffect(() => {
    fetchOrder();
  }, []);

  const fetchOrder = async () => {
    // Use the id from URL params instead of query params
    if (!id) {
      return;
    }

    try {
      setLoading(true);
      const { data, status } = await axios.get(
        `${process.env.REACT_APP_API_URL}/orders/${id}/`,
        {
          headers: {
            Authorization: `JWT ${window.localStorage.getItem("access")}`,
          },
        },
      );
      if (status === 200) {
        console.log("Fetched order data:", data);
        const activeDriverId =
          data.status === orderStatus.ORDER_ACTIVE && data.driver
            ? data.driver.id
            : null;

        setShowOnMap({
          order: {
            from: data.location_from,
            to: data.location_to,
            route_polyline: data.route_polyline || null,
          },
          orderDetailDriverId: activeDriverId,
        });
        setOrder(data);
        setLocalOrder(data);
        fetchContacts(data);
      }
    } catch (error) {
      console.error("Error fetching order details:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchContacts = async (order) => {
    try {
      const { data, status } = await axios.get(
        `${process.env.REACT_APP_API_URL}/auth/profile/`,
        {
          headers: {
            Authorization: `JWT ${window.localStorage.getItem("access")}`,
          },
        },
      );
      if (status === 200) {
        const _profile = data.find((p) => p.user.id === order.owner);
        setProfile(_profile);
      }
    } catch (error) {
      console.error("Error fetching contacts details:", error);
    }
  };

  const publishOrder = async (order) => {
    setLoading(true);
    try {
      const { status } = await axios({
        url: `${process.env.REACT_APP_API_URL}/orders/${order.uuid}/`,
        data: {
          status: orderStatus.ORDER_CREATED,
        },
        method: "PATCH",
        headers: {
          Authorization: `JWT ${window.localStorage.getItem("access")}`,
        },
      });
      if (status === 200) {
        alert("Груз успешно опубликован!");
        navigate("/my-orders");
      }
    } catch (error) {
      console.log("Error deleting order:", error);
      alert("Ошибка при удалении груза");
    } finally {
      setLoading(false);
    }
  };

  const deleteOrder = async () => {
    setLoading(true);
    try {
      const { status } = await axios({
        url: `${process.env.REACT_APP_API_URL}/orders/${order.uuid}/`,
        data: {
          status: orderStatus.ORDER_REMOVED,
        },
        method: "PATCH",
        headers: {
          Authorization: `JWT ${window.localStorage.getItem("access")}`,
        },
      });
      if (status === 200) {
        alert("Груз успешно удален!");
        navigate("/my-orders?selected=archive");
      }
    } catch (error) {
      console.log("Error deleting order:", error);
      alert("Ошибка при удалении груза");
    } finally {
      setLoading(false);
    }
  };

  const destroyOrder = async () => {
    setLoading(true);
    try {
      const { status } = await axios({
        url: `${process.env.REACT_APP_API_URL}/orders/${order.uuid}/`,
        method: "DELETE",
        headers: {
          Authorization: `JWT ${window.localStorage.getItem("access")}`,
        },
      });
      if (status === 200) {
        alert("Предложение успешно удалено!");
        navigate("/my-orders");
      }
    } catch (error) {
      console.log("Error accepting offer:", error);
      alert("Ошибка при удалении предложения");
    } finally {
      setLoading(false);
    }
  };

  // Show loading state while fetching order data
  if (loading) {
    return (
      <div className="select-transport order-detail">
        <DetailPageTitle title="Детали Груза" onBack={handleBack} />
        <div style={{ textAlign: "center", padding: "20px" }}>
          <div className="transport-search-full-screen">
            <Flex align="center" gap="middle">
              <Spin
                indicator={<LoadingOutlined style={{ fontSize: 48 }} spin />}
              />
            </Flex>
          </div>
          <p>Загрузка информации о заказе...</p>
        </div>
      </div>
    );
  }

  // Show error state if order is null after attempting to fetch
  if (!order) {
    return (
      <div className="select-transport order-detail">
        <DetailPageTitle title="Детали Груза" onBack={handleBack} />
        <div style={{ textAlign: "center", padding: "20px" }}>
          <h3>Заказ не найден</h3>
          <Button
            type="primary"
            onClick={handleBack}
          >
            Вернуться на главную
          </Button>
        </div>
      </div>
    );
  }

  let my = false;
  if (order.owner == user.id) {
    my = true;
  }

  const content = (
    <div className="select-transport order-detail">
      <DetailPageTitle title="Детали Груза" onBack={handleBack} />
      <div className="select-transport__wrapper">
        {order && (
          <OrderShort
            order={order}
            setShowOnMap={setShowOnMap}
            profile={profile}
          />
        )}
        <div className="order-detail__wrapper">
          {order.from_date && order.to_date && (
            <div
              className="select-transport__dates"
              style={{ marginBottom: "0" }}
            >
              <div className="select-transport__dates__icon" />
              <div className="order-detail__section__item__label">
                Дата погрузки:
              </div>
              <div className="order-detail__section__item__value">
                с {dayjs(order.from_date).locale("ru").format("DD MMMM")} по{" "}
                {dayjs(order.to_date).locale("ru").format("DD MMMM")}
              </div>
            </div>
          )}
          {/* <div className="order-detail__row order-detail__dates">
          {order.from_date && order.to_date && (
            <>Дата погрузки: с {dayjs(order.from_date).locale('ru').format('DD MMMM')} по {dayjs(order.to_date).locale('ru').format('DD MMMM')}</>
          )}
        </div> */}
          {/* matching market/offers are owner-only on the backend (403 for anyone else) */}
          {my && <MarketPanel orderId={id} orderStatus={order?.status} />}
          {my && <OffersPanel orderId={id} orderStatus={order?.status} />}
          <div className="order-form__section__title">
            <span className="order-form__section__title__text">О грузе</span>
          </div>
          <div className="order-detail__section">
            <div className="order-detail__section__item">
              <div className="order-detail__section__item__label">Вес:</div>
              <div className="order-detail__section__item__value">
                {order?.item?.weight} тонн.
              </div>
            </div>
            <div className="order-detail__section__item">
              <div className="order-detail__section__item__label">Объем:</div>
              <div className="order-detail__section__item__value">
                {order?.item?.volume} м<sup>3</sup>
              </div>
            </div>
            <div className="order-detail__section__item">
              <div className="order-detail__section__item__label">Груз:</div>
              <div className="order-detail__section__item__value">
                {orderType(order.item.type)}
              </div>
            </div>
            <div className="order-detail__section__item">
              <div className="order-detail__section__item__label">
                Тип груза и упаковка:
              </div>
              <div className="order-detail__section__item__value">
                {packageType(order.package_type)}
              </div>
            </div>
            {(order.item.length || order.item.width || order.item.height) && (
              <div className="order-detail__section__item">
                {order.item.length && (
                  <div className="order-detail__section__item__label">Д:</div>
                )}
                {order.item.length && (
                  <div className="order-detail__section__item__value">
                    {order.item.length}
                  </div>
                )}
                {order.item.length && (
                  <div className="order-detail__section__item__label">
                    &nbsp;&nbsp;x&nbsp;
                  </div>
                )}
                {order.item.width && (
                  <div className="order-detail__section__item__label">Ш:</div>
                )}
                {order.item.width && (
                  <div className="order-detail__section__item__value">
                    {order.item.width}
                  </div>
                )}
                {order.item.length && (
                  <div className="order-detail__section__item__label">
                    &nbsp;&nbsp;x&nbsp;
                  </div>
                )}
                {order.item.height && (
                  <div className="order-detail__section__item__label">В:</div>
                )}
                {order.item.height && (
                  <div className="order-detail__section__item__value">
                    {order.item.height}
                  </div>
                )}
              </div>
            )}
            {order.temperature_max && order.temperature_min && (
              <div className="order-detail__section__item">
                <div className="order-detail__section__item__label">
                  Температурный режим:
                </div>
                <div className="order-detail__section__item__value">
                  {order.temperature_min}°C До {order.temperature_max}°C
                </div>
              </div>
            )}
            {order.cargo_price && order.cargo_price_currency && (
              <div className="order-detail__section__item">
                <div className="order-detail__section__item__label">
                  Стоимость груза:
                </div>
                <div className="order-detail__section__item__value">
                  {order.cargo_price} {currency(order.cargo_price_currency)}
                </div>
              </div>
            )}
          </div>
          <div className="order-form__section__title">
            <span className="order-form__section__title__text">
              Требования к транспортировке
            </span>
          </div>
          <div className="order-detail__section">
            <div className="order-detail__section__item">
              <div className="order-detail__section__item__label">
                Тип перевозки:
              </div>
              <div className="order-detail__section__item__value">
                {shippingType(order.type_of_transportation)}
              </div>
            </div>
            {order.item.count_of_transport &&
              (+order.type_of_transportation === 1 ||
                +order.type_of_transportation === 3) && (
                <div className="order-detail__section__item">
                  <div className="order-detail__section__item__label">
                    Кол-во машин:
                  </div>
                  <div className="order-detail__section__item__value">
                    {order.item.count_of_transport} шт.
                  </div>
                </div>
              )}
            <div className="order-detail__section__item">
              <div className="order-detail__section__item__label">
                Категория авто:
              </div>
              <div className="order-detail__section__item__value">
                {transportType(order.transport_category).label}
              </div>
            </div>
            <div className="order-detail__section__item">
              <div className="order-detail__section__item__label">
                Тип кузова:
              </div>
              <div className="order-detail__section__item__value">
                {bodyType(order.transport_body_type)}
              </div>
            </div>
            <div className="order-detail__section__item">
              <div className="order-detail__section__item__label">
                Тип загрузки:
              </div>
              <div className="order-detail__section__item__value">
                {order.load_type &&
                  order.load_type.length > 0 &&
                  order.load_type.map((item) => {
                    return <span key={item}>{loadType(item)}&nbsp;&nbsp;</span>;
                  })}
              </div>
            </div>
          </div>
          <div className="order-form__section__title">
            <span className="order-form__section__title__text">
              Дополнительные условия
            </span>
          </div>
          {order.comment && (
            <div
              className="order-form__section__text"
              style={{
                fontWeight: "bold",
                minHeight: "100px",
                border: "1px solid #ccc",
                borderRadius: "5px",
                padding: "10px",
              }}
            >
              {order.comment && (
                <div className="order-detail__section__item">
                  <div className="order-detail__section__item__value">
                    {order.comment}
                  </div>
                </div>
              )}
            </div>
          )}
          {!order.comment && (
            <div className="no-comment">
              Заказчик не написал условия. Если есть вопросы уточните
            </div>
          )}
          {/* <div className="order-detail__section">
          <div className="order-detail__section__item">
            {order.comment &&<div className="order-detail__section__item__value">
              {order.comment}
            </div>}
          </div>
        </div> */}

          <div className="order-form__section__title">
            <span className="order-form__section__title__text">
              Ставка и оплата
            </span>
          </div>
          <div className="order-detail__section">
            <div className="order-detail__section__item">
              <div className="order-detail__section__item__label">
                Стоимость перевозки:
              </div>
              <div className="order-detail__section__item__value">
                {order.price
                  ? `${order.price} ${currency(order.currency)}`
                  : "Ждем предложения"}
                &nbsp;{order.nds ? "с НДС" : "без НДС"}
              </div>
            </div>
            <div className="order-detail__section__item">
              <div className="order-detail__section__item__label">
                Условия оплаты:
              </div>
              <div className="order-detail__section__item__value">
                {paymentForms[order.payment_form]}
              </div>
            </div>
            <div className="order-detail__section__item">
              <div className="order-detail__section__item__label">
                Способ оплаты:
              </div>
              <div className="order-detail__section__item__value">
                {paymentMethods[order.payment_method]}
              </div>
            </div>
          </div>
          <ContactCompanyInfo
            companyName={order.contacts.company_name}
            contactName={order.contacts.contact_name}
            phoneNumber={order.contacts.phone_number}
            whatsappNumber={profile?.whatsapp_number}
            telegramNickname={profile?.telegram_nickname}
            okpo={profile?.company_profile?.OKPO}
            inn={profile?.company_profile?.INN}
            certificate={profile?.company_profile?.certificate}
            license={profile?.company_profile?.license}
            showTitle={true}
            avatar={
              profile?.company_profile?.profile_images || order.owner?.avatar
            }
          />
          {!(order.status === orderStatus.ORDER_ACTIVE && order.driver) &&
            my &&
            !order.offer &&
            !order.suggestion && (
              <div className="transport__show-onmap transport__controls pd-25">
                {(order.status === orderStatus.ORDER_CREATED ||
                  order.status === orderStatus.ORDER_DRAFT) && (
                    <Button
                      type="primary"
                      style={{ marginRight: "8px" }}
                      onClick={() => {
                        // Navigate to the new edit route
                        navigate(`/orders/edit/${order.uuid}`);
                      }}
                    >
                      Редактировать
                    </Button>
                  )}
                {!order.suggestion &&
                  order.status !== orderStatus.ORDER_DRAFT && (
                    <Button
                      type="default"
                      style={{
                        marginRight: "8px",
                        backgroundColor: "#27AE60",
                        borderColor: "#27AE60",
                        color: "white",
                      }}
                      onClick={() => {
                        // Navigate to the dedicated duplicate route
                        navigate(`/orders/duplicate/${order.uuid}`);
                      }}
                      className="green-button"
                    >
                      Дублировать
                    </Button>
                  )}
                {(order.status === orderStatus.ORDER_CREATED ||
                  order.status === orderStatus.ORDER_DRAFT) && (
                    <Button danger onClick={deleteOrder}>
                      Удалить
                    </Button>
                  )}
                {order.status === orderStatus.ORDER_REMOVED && (
                  <Button danger onClick={destroyOrder}>
                    Удалить
                  </Button>
                )}
              </div>
            )}
          {/* <div className="order-detail__section">
          <div className="order-detail__section__item">
            <div className="order-detail__section__item__label">Название компании:</div>
            <div className="order-detail__section__item__value">{order.contacts.company_name}</div>
          </div>
          <div className="order-detail__section__item">
            <div className="order-detail__section__item__label">Контактное лицо:</div>
            <div className="order-detail__section__item__value">{order.contacts.contact_name}</div>
          </div>
          <div className="order-detail__section__item">
            <div className="order-detail__section__item__label">Контактный номер:</div>
            <div className="order-detail__section__item__value">{order.contacts.phone_number}</div>
          </div>
        </div> */}
        </div>
      </div>
    </div>
  );

  if (isMobile) {
    return (
      <Drawer
        placement="bottom"
        closable={false}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        height="auto"
        push={false}
        className="mobile-only"
        rootClassName="mobile-drawer-menu-container mobile-drawer-other-level"
        styles={{
          body: { padding: '10px 15px 0', backgroundColor: '#F0F4F7' }
        }}
      >
        <div style={{ maxHeight: 'calc(100vh - 10px)', overflowY: 'auto' }}>
          {content}
        </div>
      </Drawer>
    );
  }

  return content;
};

export default OrderDetail;
